/**
 * Dispatch's "Update Leg Status" modal.
 *
 * One row per leg, showing its current status and the moves allowed on it.
 * What's allowed comes from the shared rules helper
 * (`@/lib/leg-status-actions`) the driver app also reads, and the write goes
 * through the shared `useLegStatusProgression` hook in status-only mode:
 * leg + order rollup + audit row, no trailer moves, no yard events, no
 * customer emails.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CancelOrderDialog,
  type CancelConfirmPayload,
} from "@/components/orders/CancelOrderDialog";
import type { LegStatusMove, LegStatusPlan } from "@/lib/leg-status-actions";

interface LegStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: LegStatusPlan;
  isPending: boolean;
  pendingLegId: string | null;
  onMove: (legId: string, move: LegStatusMove) => void;
  /** Cancel just this stop (and its external trip), with a reason. */
  onCancelLeg?: (legId: string, payload: CancelConfirmPayload) => void;
  /** Shown in the reason prompt so dispatch knows what they're cancelling. */
  orderUniqueId?: string;
}

const STATUS_LABEL: Record<string, string> = {
  booked: "Booked",
  confirmed: "Confirmed",
  in_transit: "In Transit",
  delivered: "Delivered",
  canceled: "Cancelled",
  tender_received: "Tender received",
};

export function LegStatusDialog({
  open,
  onOpenChange,
  plan,
  isPending,
  pendingLegId,
  onMove,
  onCancelLeg,
  orderUniqueId,
}: LegStatusDialogProps) {
  const [cancelLegId, setCancelLegId] = useState<string | null>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Update leg status</DialogTitle>
          <DialogDescription>
            Move a leg forward when the driver couldn't. This updates the leg,
            the order and the history only — no trailer moves or customer emails.
          </DialogDescription>
        </DialogHeader>

        <TooltipProvider>
          <ul className="space-y-2">
            {plan.legs.map((leg) => {
              const busy = isPending && pendingLegId === leg.legId;
              return (
                <li
                  key={leg.legId}
                  className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        Leg {leg.legSequence ?? "—"}
                      </span>
                      <Badge variant="secondary">
                        {STATUS_LABEL[leg.status ?? ""] ?? leg.status ?? "Unknown"}
                      </Badge>
                    </div>
                    {leg.locked && (
                      <p className="text-xs text-muted-foreground">
                        This leg is closed and can't be changed.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0 sm:justify-end">

                    {leg.actions.map((action) => {
                      const isCancel = action.move === "canceled";
                      if (isCancel && !onCancelLeg) return null;
                      const button = (
                        <Button
                          key={action.move}
                          size="sm"
                          className="whitespace-nowrap"
                          variant={isCancel ? "destructive" : "outline"}
                          disabled={!action.allowed || isPending}
                          onClick={() =>
                            isCancel
                              ? setCancelLegId(leg.legId)
                              : onMove(leg.legId, action.move)
                          }
                        >
                          {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            action.label
                          )}
                        </Button>
                      );
                      if (action.allowed) return button;
                      return (
                        <Tooltip key={action.move}>
                          <TooltipTrigger asChild>
                            <span tabIndex={0}>{button}</span>
                          </TooltipTrigger>
                          <TooltipContent>{action.reason}</TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
        </TooltipProvider>

        {onCancelLeg && (
          <CancelOrderDialog
            open={!!cancelLegId}
            onOpenChange={(v) => {
              if (!v) setCancelLegId(null);
            }}
            orderId={orderUniqueId ?? ""}
            isPending={isPending}
            onConfirm={(payload) => {
              const legId = cancelLegId;
              setCancelLegId(null);
              if (legId) onCancelLeg(legId, payload);
            }}
          />
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
